/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { AnyAction, SerializedError } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';
import { Dispatch } from 'redux';

import * as api from '@ibm-watson/shared/modules/actions/api/entities/entities';
import * as operations from '@ibm-watson/shared/modules/ia/store/operations';
import { ctaEvents } from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import { SystemEntity } from '../../../../ts/state';
import { selectIsAccessUpdatesEnabled } from '../../../ia/store/features/selectors';
import { selectIsNewIa } from '../../../ia/store/instance/selectors';
import { getAssistantId } from '../../../ia/store/session/selectors';
import * as actionTypes from './actionTypes';
import { systemEntitiesSchema, systemEntitySchema } from './schema';
import { getById } from './selectors';

// --------------------------------------------- //
export const fetchSystemEntitiesStart = (skillId: string): AnyAction => ({
  type: actionTypes.FETCH_SYSTEM_ENTITIES_START,
  skillId
});

export const fetchSystemEntitiesSuccess = (response: any): AnyAction => ({
  type: actionTypes.FETCH_SYSTEM_ENTITIES_SUCCESS,
  response,
  payload: { operation: operations.LIST }
});

export const fetchSystemEntitiesError = (
  error: SerializedError
): AnyAction => ({
  type: actionTypes.FETCH_SYSTEM_ENTITIES_ERROR,
  error
});

export const fetchSystemEntities =
  (skillId: string, reactivateSession: boolean) =>
  async (dispatch: Dispatch, getState) => {
    dispatch(fetchSystemEntitiesStart(skillId));
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to listSystemEntities
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    try {
      const response = await api.listSystemEntities(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        reactivateSession
      );
      dispatch(
        fetchSystemEntitiesSuccess(
          normalize(response.entities, systemEntitiesSchema)
        )
      );
    } catch (error) {
      dispatch(fetchSystemEntitiesError(error));
      throw error;
    }
  };

// --------------------------------------------- //

export const createSystemEntityStart = (
  skillId: string,
  entity: SystemEntity
): AnyAction => ({
  type: actionTypes.CREATE_SYSTEM_ENTITY_START,
  skillId,
  entity
});

export const createSystemEntitySuccess = (response: any): AnyAction => ({
  type: actionTypes.CREATE_SYSTEM_ENTITY_SUCCESS,
  response,
  payload: { operation: operations.CREATE }
});

export const createSystemEntityError = (error: SerializedError): AnyAction => ({
  type: actionTypes.CREATE_SYSTEM_ENTITY_ERROR,
  error
});

export const createSystemEntity =
  (skillId: string, entity: SystemEntity) =>
  async (dispatch: Dispatch, getState) => {
    dispatch(createSystemEntityStart(skillId, entity));
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to createVariable
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    try {
      const response = await api.create(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        entity
      );
      entity.source = 'system.entities';
      // we need to merge the existing system entity with the one returned from the api call
      // the returned sys entity does not have a description
      const sysEntity = getById(getState(), entity.entity);
      const merged = { ...sysEntity, ...entity };
      dispatch(
        createSystemEntitySuccess(normalize(merged, systemEntitySchema))
      );
      ctaEvents.entitiesSystem({
        category: entity.entity,
        successFlag: true,
        resultValue: 'on'
      });
      return response;
    } catch (error) {
      dispatch(createSystemEntityError(error));
      ctaEvents.entitiesSystem({
        category: entity.entity,
        successFlag: false,
        resultValue: 'on'
      });
      throw error;
    }
  };

// --------------------------------------------- //

export const deleteSystemEntityStart = (): AnyAction => ({
  type: actionTypes.DELETE_SYSTEM_ENTITY_START
});

export const deleteSystemEntitySuccess = (response: any): AnyAction => ({
  type: actionTypes.DELETE_SYSTEM_ENTITY_SUCCESS,
  response,
  payload: { operation: operations.UPDATE } //we are not actually deleting the system entity - just updating its enablement
});

export const deleteSystemEntityError = (error: SerializedError): AnyAction => ({
  type: actionTypes.DELETE_SYSTEM_ENTITY_ERROR,
  error
});

export const deleteSystemEntity =
  (skillId: string, entity: SystemEntity) =>
  async (dispatch: Dispatch, getState) => {
    dispatch(deleteSystemEntityStart());
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to createVariable
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    try {
      const response = await api.deleteEntity(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        entity
      );
      delete entity.source;
      dispatch(
        deleteSystemEntitySuccess(normalize({ ...entity }, systemEntitySchema))
      );
      ctaEvents.entitiesSystem({
        category: entity.entity,
        successFlag: true,
        resultValue: 'off'
      });
      return response;
    } catch (error) {
      dispatch(deleteSystemEntityError(error));
      ctaEvents.entitiesSystem({
        category: entity.entity,
        successFlag: false,
        resultValue: 'off'
      });
      throw error;
    }
  };
